import { RxDocument } from "rxdb"
import { AttachmentDocument } from "src/db/collectionDefinitions/csi/attachment"
import { AttributeSelectDocumentMethods } from "src/db/collectionDefinitions/csi/attribute"
import { CorrectiveWorkOnItemDocument } from "src/db/collectionDefinitions/csi/correctiveWork"
import { IssueOnItemActionsDocument } from "src/db/collectionDefinitions/csi/issueOnItemActions"
import { IssueOnItemCommentDocument } from "src/db/collectionDefinitions/csi/issueOnItemComments"
import { IssuePriorityDocument } from "src/db/collectionDefinitions/csi/issuePriorities"
import { IssueOnItemDocument } from "src/db/collectionDefinitions/csi/issuesOnItem"
import { ItemDocument } from "src/db/collectionDefinitions/csi/item"
import { RegularWorkOnItemDocument } from "src/db/collectionDefinitions/csi/regularWork"
import { WorkDomainWorkspaceDocument } from "src/db/collectionDefinitions/csi/workDomainWorkspace"
import {
  WorkOrderCorrectiveItemDocument,
  WorkOrderRegularItemDocument,
} from "src/db/collectionDefinitions/csi/workOrder"
import {
  WorkOrderCorrectiveItemCommentDocument,
  WorkOrderRegularItemCommentDocument,
} from "src/db/collectionDefinitions/csi/workOrderComments"
import { WorkOrderPriorityDocument } from "src/db/collectionDefinitions/csi/workOrderPriority"
import { UserRoleDocument } from "src/db/collectionDefinitions/springboard/userRole"
import { WorkDomainGlobalDocument } from "src/db/collectionDefinitions/springboard/workDomainGlobal"
import { AppBaseDB, AppCommonDB, getCommonDB, getDB } from "src/db/databases"
import { useApiCallStore } from "src/stores/apiCallStore"
import { Log } from "src/util/classes/log"
import { CSIurl, SpringboardURL } from "src/util/enums/urls"
import { getAttachmentsList } from "src/util/http/csi/attachments"
import {
  getAttributeCharList,
  getAttributeNumericList,
  getAttributeSelectList,
  getAttributesSelectOptionsList,
  getAttributeTemporalList,
} from "src/util/http/csi/attributes"
import { getIdentifiersList } from "src/util/http/csi/identifiers"
import { getIssuePrioritiesList } from "src/util/http/csi/issuePriorities"
import {
  getIssueOnItemAttachment,
  getIssueOnItemAttachmentContent,
  getIssueOnItemAttachmentList,
  getIssueOnItemCommentAttachment,
  getIssueOnItemCommentAttachmentContent,
  getIssueOnItemCommentAttachmentList,
  getIssueOnItemCommentList,
  getIssuesOnItemActionsList,
  getIssuesOnItemList,
  postIssueOnItemAttachment,
  postIssueOnItemAttachmentBatch,
  postIssueOnItemCommentAttachment,
  postIssueOnItemCommentAttachmentBatch,
} from "src/util/http/csi/issues"
import { getItemClassesList } from "src/util/http/csi/itemClasses"
import {
  getItemModelAttachment,
  getItemModelAttachmentContent,
  getItemModelAttachmentList,
  getItemModelsList,
  postItemModelAttachment,
  postItemModelAttachmentBatch,
} from "src/util/http/csi/itemModels"
import {
  getItemAttachment,
  getItemAttachmentContent,
  getItemAttachmentList,
  getItemAttributeCharValuesList,
  getItemAttributeNumericValuesList,
  getItemAttributeSelectValuesList,
  getItemAttributeTemporalValuesList,
  getItemsList,
  getWorkOrderListForItem,
  postItemAttachment,
  postItemAttachmentBatch,
} from "src/util/http/csi/items"
import {
  getCorrectiveWorkOnItemAttachment,
  getCorrectiveWorkOnItemAttachmentContent,
  getCorrectiveWorkOnItemAttachmentList,
  getCorrectiveWorkOnItemCommentAttachment,
  getCorrectiveWorkOnItemCommentAttachmentContent,
  getCorrectiveWorkOnItemCommentAttachmentList,
  getCorrectiveWorkOnItemList,
  getRegularWorkOnItemAttachment,
  getRegularWorkOnItemAttachmentContent,
  getRegularWorkOnItemAttachmentList,
  getRegularWorkOnItemCommentAttachment,
  getRegularWorkOnItemCommentAttachmentContent,
  getRegularWorkOnItemCommentAttachmentList,
  getRegularWorkOnItemList,
  postCorrectiveWorkOnItemAttachment,
  postCorrectiveWorkOnItemAttachmentBatch,
  postCorrectiveWorkOnItemCommentAttachment,
  postCorrectiveWorkOnItemCommentAttachmentBatch,
  postRegularWorkOnItemAttachment,
  postRegularWorkOnItemAttachmentBatch,
  postRegularWorkOnItemCommentAttachment,
  postRegularWorkOnItemCommentAttachmentBatch,
} from "src/util/http/csi/performedWork"
import { getWorkDomainsWorkspaceList } from "src/util/http/csi/workDomainsWorkspace"
import { getWorkOrderPrioritiesList } from "src/util/http/csi/workOrderPriorities"
import {
  getWorkOrderCorrectiveItemAttachment,
  getWorkOrderCorrectiveItemAttachmentContent,
  getWorkOrderCorrectiveItemAttachmentList,
  getWorkOrderCorrectiveItemCommentAttachment,
  getWorkOrderCorrectiveItemCommentAttachmentContent,
  getWorkOrderCorrectiveItemCommentAttachmentList,
  getWorkOrderCorrectiveItemCommentList,
  getWorkOrderCorrectiveItemList,
  getWorkOrderRegularItemAttachment,
  getWorkOrderRegularItemAttachmentContent,
  getWorkOrderRegularItemAttachmentList,
  getWorkOrderRegularItemCommentAttachment,
  getWorkOrderRegularItemCommentAttachmentContent,
  getWorkOrderRegularItemCommentAttachmentList,
  getWorkOrderRegularItemCommentList,
  getWorkOrderRegularItemList,
  postWorkOrderCorrectiveItemAttachment,
  postWorkOrderCorrectiveItemAttachmentBatch,
  postWorkOrderCorrectiveItemCommentAttachment,
  postWorkOrderCorrectiveItemCommentAttachmentBatch,
  postWorkOrderRegularItemAttachment,
  postWorkOrderRegularItemAttachmentBatch,
  postWorkOrderRegularItemCommentAttachment,
  postWorkOrderRegularItemCommentAttachmentBatch,
} from "src/util/http/csi/workOrders"
import {
  getAllAttachmentForEntity,
  getAllEntitiesConcurrent,
  getAllEntitiesForIdConcurrent,
  isIgnoredError,
} from "src/util/http/httpHelper"
import { getUserRoles } from "src/util/http/springboard/user"
import { getWorkDomainsGlobalList } from "src/util/http/springboard/workDomainsGlobal"
import { getUsersForWorkspace, getWorkspaceList } from "src/util/http/springboard/workspaces"
import {
  Attachment,
  AttachmentContext,
  AttachmentUpload,
} from "src/util/types/dataModels/attachment"
import {
  AttributeChar,
  AttributeNumeric,
  AttributeSelect,
  AttributeSelectOptions,
  AttributeTemporal,
  ItemAttributeCharValue,
  ItemAttributeNumericValue,
  ItemAttributeSelectValue,
  ItemAttributeTemporalValue,
} from "src/util/types/dataModels/attribute"
import { BatchErrors, ObjectList } from "src/util/types/dataModels/genericObject"
import { HttpConfig } from "src/util/types/dataModels/http"
import { IssuesOnItem, IssuesOnItemAction } from "src/util/types/dataModels/issue"
import { IssueComment } from "src/util/types/dataModels/issueComment"
import { IssuePriority } from "src/util/types/dataModels/issuePriority"
import { Item, ItemWorkOrder } from "src/util/types/dataModels/item"
import { ItemClass } from "src/util/types/dataModels/itemClass"
import { ItemModel } from "src/util/types/dataModels/itemModel"
import { CorrectiveWorkOnItem, RegularWorkOnItem } from "src/util/types/dataModels/performedWork"
import { UserRole } from "src/util/types/dataModels/user"
import { WorkDomainGlobal, WorkDomainWorkspace } from "src/util/types/dataModels/workDomain"
import { WorkOrderCorrectiveItem, WorkOrderRegularItem } from "src/util/types/dataModels/workOrder"
import {
  WorkOrderCorrectiveItemComment,
  WorkOrderRegularItemComment,
} from "src/util/types/dataModels/workOrderComment"
import { WorkOrderPriority } from "src/util/types/dataModels/workOrderPriority"

// NOTE: Possible improvements:
// - Force fetch that would bypass the canCall again check.
// - Filtering.
// - Functions for singular items

// NOTE: Naming convention:
// Update entity from the API -> "fetch" + entity name + "List" (if multiple/array) + "Data" = f.x. fetchItemListData
// Get entity from the DB -> "get" + entity name + "List" (if multiple/array) + "Data" = f.x. getItemListData

// TODO: HANDLE DELETION OF ENTITIES ON THE SERVER :'(

//region Workspace

export async function fetchWorkspaceListData(db?: AppCommonDB): Promise<void> {
  if (!db) db = await getCommonDB()

  try {
    // NOTE: Workspace don't support update_dt
    const entities = await getWorkspaceList()
    await db.workspace.bulkUpsert(entities)
  } catch (error) {
    Log.error({
      message: "Error trying to fetch Workspace data",
      error,
    })

    throw error
  }
}

export async function getWorkspaceListData() {
  const db = await getCommonDB()
  await db.workspace?.remove() // Kills the db instance, so we have config,to recreate it
  const newDb = await getCommonDB()
  await fetchWorkspaceListData(newDb)
  return await newDb.workspace.find().exec()
}

export async function fetchUsersForWorkspaceListData(
  workspaceId: string,
  config: HttpConfig = {},
  db?: AppCommonDB
): Promise<boolean> {
  if (!db) db = await getCommonDB()

  try {
    // NOTE: Workspace don't support update_dt
    const entities = await getUsersForWorkspace(config, workspaceId)
    if (entities.length > 0) {
      await db.user.bulkUpsert(entities)
      return true
    }
  } catch (error) {
    Log.error({
      message: "Error trying to fetch Workspace data",
      error,
    })

    throw error
  }

  return false
}

export async function getUsersForWorkspaceListData(workspaceId: string, config?: HttpConfig) {
  const db = await getCommonDB()
  await fetchUsersForWorkspaceListData(workspaceId, config, db)
  return await db.user.find().exec()
}

//endregion

//region User Roles

export async function fetchUserRolesListData(
  config?: HttpConfig,
  db?: AppCommonDB
): Promise<boolean> {
  const apiCallStore = useApiCallStore()
  const callUrl = SpringboardURL.USER_ROLES

  try {
    if (apiCallStore.canCallAgain(callUrl)) {
      if (!db) db = await getCommonDB()

      // NOTE: User roles don't support update_dt
      const entities = await getAllEntitiesConcurrent<UserRole>(
        getUserRoles,
        { lastCall: undefined, callUrl },
        config
      )

      if (entities.length > 0) {
        await db.user_role.bulkUpsert(entities)
        return true
      }
    }
  } catch (error) {
    if (isIgnoredError(error)) return false
    Log.error({
      message: "Error trying to fetch User Roles data",
      error,
    })

    throw error
  }

  return false
}

export async function getUserRolesListData(config?: HttpConfig): Promise<UserRoleDocument[]> {
  const db = await getCommonDB()
  await fetchUserRolesListData(config, db)
  return await db.user_role.find().exec()
}

//endregion

//region Item

export async function fetchItemListData(config?: HttpConfig, db?: AppBaseDB): Promise<boolean> {
  const apiCallStore = useApiCallStore()
  const callUrl = CSIurl.ITEMS

  try {
    if (apiCallStore.canCallAgain(callUrl)) {
      if (!db) db = await getDB()

      const lastCall = apiCallStore.getLastUpdatedTimeStamp(callUrl)

      const entities = await getAllEntitiesConcurrent<Item>(
        getItemsList,
        { lastCall, callUrl },
        config
      )

      if (entities.length > 0) {
        await db.item.bulkUpsert(entities)
        return true
      }
    }
  } catch (error) {
    if (isIgnoredError(error)) return false
    Log.error({
      message: "Error trying to fetch Item data",
      error,
    })

    throw error
  }

  return false
}

export async function getItemListData(config?: HttpConfig): Promise<ItemDocument[]> {
  const db = await getDB()
  await fetchItemListData(config, db)
  return await db.item.find().exec()
}

export async function fetchItemAttributeCharValuesListData(
  config?: HttpConfig,
  db?: AppBaseDB
): Promise<boolean> {
  const apiCallStore = useApiCallStore()
  const callUrl = CSIurl.ITEMS_ATTRIBUTES_CHAR_VALUES_LIST

  try {
    if (apiCallStore.canCallAgain(callUrl)) {
      if (!db) db = await getDB()

      const lastCall = apiCallStore.getLastUpdatedTimeStamp(callUrl)

      const entities = await getAllEntitiesConcurrent<ItemAttributeCharValue>(
        getItemAttributeCharValuesList,
        { lastCall, callUrl },
        config
      )

      if (entities.length > 0) {
        await db.attribute_char_value.bulkUpsert(entities)
        return true
      }
    }
  } catch (error) {
    if (isIgnoredError(error)) return false
    Log.error({
      message: "Error trying to fetch Item attribute char data",
      error,
    })

    throw error
  }

  return false
}

export async function fetchItemAttributeNumericValuesListData(
  config?: HttpConfig,
  db?: AppBaseDB
): Promise<boolean> {
  const apiCallStore = useApiCallStore()
  const callUrl = CSIurl.ITEMS_ATTRIBUTES_NUMERIC_VALUES_LIST

  try {
    if (apiCallStore.canCallAgain(callUrl)) {
      if (!db) db = await getDB()

      const lastCall = apiCallStore.getLastUpdatedTimeStamp(callUrl)

      const entities = await getAllEntitiesConcurrent<ItemAttributeNumericValue>(
        getItemAttributeNumericValuesList,
        { lastCall, callUrl },
        config
      )

      if (entities.length > 0) {
        await db.attribute_numeric_value.bulkUpsert(entities)
        return true
      }
    }
  } catch (error) {
    if (isIgnoredError(error)) return false
    Log.error({
      message: "Error trying to fetch Item attribute numeric data",
      error,
    })

    throw error
  }

  return false
}

export async function fetchItemAttributeSelectValuesListData(
  config?: HttpConfig,
  db?: AppBaseDB
): Promise<boolean> {
  const apiCallStore = useApiCallStore()
  const callUrl = CSIurl.ITEMS_ATTRIBUTES_SELECT_VALUES_LIST

  try {
    if (apiCallStore.canCallAgain(callUrl)) {
      if (!db) db = await getDB()

      const lastCall = apiCallStore.getLastUpdatedTimeStamp(callUrl)

      const entities = await getAllEntitiesConcurrent<ItemAttributeSelectValue>(
        getItemAttributeSelectValuesList,
        { lastCall, callUrl },
        config
      )

      if (entities.length > 0) {
        await db.attribute_select_value.bulkUpsert(entities)
        return true
      }
    }
  } catch (error) {
    if (isIgnoredError(error)) return false
    Log.error({
      message: "Error trying to fetch Item attribute select data",
      error,
    })

    throw error
  }

  return false
}

export async function fetchItemAttributeTemporalValuesListData(
  config?: HttpConfig,
  db?: AppBaseDB
): Promise<boolean> {
  const apiCallStore = useApiCallStore()
  const callUrl = CSIurl.ITEMS_ATTRIBUTES_TEMPORAL_VALUES_LIST

  try {
    if (apiCallStore.canCallAgain(callUrl)) {
      if (!db) db = await getDB()

      const lastCall = apiCallStore.getLastUpdatedTimeStamp(callUrl)

      const entities = await getAllEntitiesConcurrent<ItemAttributeTemporalValue>(
        getItemAttributeTemporalValuesList,
        { lastCall, callUrl },
        config
      )

      if (entities.length > 0) {
        await db.attribute_temporal_value.bulkUpsert(entities)
        return true
      }
    }
  } catch (error) {
    if (isIgnoredError(error)) return false
    Log.error({
      message: "Error trying to fetch Item attribute temporal data",
      error,
    })

    throw error
  }

  return false
}

export async function fetchItemWorkOrderListData(
  itemId: string,
  config?: HttpConfig
): Promise<boolean> {
  const apiCallStore = useApiCallStore()
  const callUrl = CSIurl.ITEMS_WORK_ORDER_REGULAR.replace("{item_id}", itemId)

  try {
    if (apiCallStore.canCallAgain(callUrl)) {
      const db = await getDB()

      const lastCall = apiCallStore.getLastUpdatedTimeStamp(callUrl)

      const workOrders = await getAllEntitiesForIdConcurrent<WorkOrderRegularItem>(
        getWorkOrderListForItem,
        { lastCall, callUrl },
        itemId,
        config
      )

      const itemWorkOrder: ItemWorkOrder = {
        item_id: itemId,
        work_orders: workOrders,
      }
      await db.item_work_orders.upsert(itemWorkOrder)
      return true
    }
  } catch (error) {
    if (isIgnoredError(error)) return false
    Log.error({
      message: "Error trying to fetch Work Order list for itemID",
      error,
    })

    throw error
  }

  return false
}

//endregion

//region Item Class

export async function fetchItemClassListData(
  config?: HttpConfig,
  db?: AppBaseDB
): Promise<boolean> {
  const apiCallStore = useApiCallStore()
  const callUrl = CSIurl.ITEM_CLASSES_LIST

  try {
    if (apiCallStore.canCallAgain(callUrl)) {
      if (!db) db = await getDB()

      const lastCall = apiCallStore.getLastUpdatedTimeStamp(callUrl)

      const entities = await getAllEntitiesConcurrent<ItemClass>(
        getItemClassesList,
        { lastCall, callUrl },
        config
      )

      if (entities.length > 0) {
        await db.item_class.bulkUpsert(entities)
        return true
      }
    }
  } catch (error) {
    if (isIgnoredError(error)) return false
    Log.error({
      message: "Error trying to fetch Item Class data",
      error,
    })

    throw error
  }

  return false
}

export async function getItemClassListData(config?: HttpConfig): Promise<RxDocument<ItemClass>[]> {
  const db = await getDB()
  await fetchItemClassListData(config, db)
  return await db.item_class.find().exec()
}

//endregion

//region Item Model

export async function fetchItemModelListData(
  config?: HttpConfig,
  db?: AppBaseDB
): Promise<boolean> {
  const apiCallStore = useApiCallStore()
  const callUrl = CSIurl.ITEM_MODELS

  try {
    if (apiCallStore.canCallAgain(callUrl)) {
      if (!db) db = await getDB()

      const lastCall = apiCallStore.getLastUpdatedTimeStamp(callUrl)

      const entities = await getAllEntitiesConcurrent<ItemModel>(
        getItemModelsList,
        { lastCall, callUrl },
        config
      )

      if (entities.length > 0) {
        await db.item_model.bulkUpsert(entities)
        return true
      }
    }
  } catch (error) {
    if (isIgnoredError(error)) return false
    Log.error({
      message: "Error trying to fetch Item Model data",
      error,
    })

    throw error
  }

  return false
}

export async function getItemModelListData(config?: HttpConfig): Promise<RxDocument<ItemModel>[]> {
  const db = await getDB()
  await fetchItemModelListData(config, db)
  return await db.item_model.find().exec()
}

//endregion

//region Issue

export async function fetchIssuePriorityListData(
  config?: HttpConfig,
  db?: AppBaseDB
): Promise<boolean> {
  const apiCallStore = useApiCallStore()
  const callUrl = CSIurl.ISSUE_PRIORITIES

  try {
    if (apiCallStore.canCallAgain(callUrl)) {
      if (!db) db = await getDB()

      const lastCall = apiCallStore.getLastUpdatedTimeStamp(callUrl)

      const entities = await getAllEntitiesConcurrent<IssuePriority>(
        getIssuePrioritiesList,
        { lastCall, callUrl },
        config
      )

      if (entities.length > 0) {
        await db.issue_priority.bulkUpsert(entities)
        return true
      }
    }
  } catch (error) {
    if (isIgnoredError(error)) return false
    Log.error({
      message: "Error trying to fetch Issue Priorities data",
      error,
    })

    throw error
  }

  return false
}

export async function getIssuePriorityListData(
  config?: HttpConfig
): Promise<IssuePriorityDocument[]> {
  const db = await getDB()
  await fetchIssuePriorityListData(config, db)
  return await db.issue_priority.find().exec()
}

export async function fetchIssueOnItemListData(
  config?: HttpConfig,
  db?: AppBaseDB
): Promise<boolean> {
  const apiCallStore = useApiCallStore()
  const callUrl = CSIurl.ISSUES_ON_ITEM

  try {
    if (apiCallStore.canCallAgain(callUrl)) {
      if (!db) db = await getDB()

      const lastCall = apiCallStore.getLastUpdatedTimeStamp(callUrl)

      const entities = await getAllEntitiesConcurrent<IssuesOnItem>(
        getIssuesOnItemList,
        { lastCall, callUrl },
        config
      )

      if (entities.length > 0) {
        await db.issues_on_item.bulkUpsert(entities)
        return true
      }
    }
  } catch (error) {
    if (isIgnoredError(error)) return false
    Log.error({
      message: "Error trying to fetch Issue On Item data",
      error,
    })

    throw error
  }

  return false
}

export async function getIssueOnItemListData(config?: HttpConfig): Promise<IssueOnItemDocument[]> {
  const db = await getDB()
  await fetchIssueOnItemListData(config, db)
  return await db.issues_on_item.find().exec()
}

export async function fetchIssueOnItemActionsListData(
  config?: HttpConfig,
  db?: AppBaseDB
): Promise<boolean> {
  const apiCallStore = useApiCallStore()
  const callUrl = CSIurl.ISSUE_ON_ITEM_ACTIONS

  try {
    if (apiCallStore.canCallAgain(callUrl)) {
      if (!db) db = await getDB()

      const lastCall = apiCallStore.getLastUpdatedTimeStamp(callUrl)

      const entities = await getAllEntitiesConcurrent<IssuesOnItemAction>(
        getIssuesOnItemActionsList,
        { lastCall, callUrl },
        config
      )

      if (entities.length > 0) {
        await db.issue_on_item_actions.bulkUpsert(entities)
        return true
      }
    }
  } catch (error) {
    if (isIgnoredError(error)) return false
    Log.error({
      message: "Error trying to fetch the Actions data for this Issue On Item",
      error,
    })

    throw error
  }

  return false
}

export async function getIssueOnItemActionsListData(
  config?: HttpConfig
): Promise<IssueOnItemActionsDocument[]> {
  const db = await getDB()
  await fetchIssueOnItemActionsListData(config, db)
  return await db.issue_on_item_actions.find().exec()
}

export async function fetchIssueOnItemCommentListData(
  config?: HttpConfig,
  db?: AppBaseDB
): Promise<boolean> {
  const apiCallStore = useApiCallStore()
  const callUrl = CSIurl.ISSUES_ON_ITEM_COMMENTS

  try {
    if (apiCallStore.canCallAgain(callUrl)) {
      if (!db) db = await getDB()

      const lastCall = apiCallStore.getLastUpdatedTimeStamp(callUrl)

      const entities = await getAllEntitiesConcurrent<IssueComment>(
        getIssueOnItemCommentList,
        { lastCall, callUrl },
        config
      )

      if (entities.length > 0) {
        await db.issue_on_item_comments.bulkUpsert(entities)
        return true
      }
    }
  } catch (error) {
    if (isIgnoredError(error)) return false
    Log.error({
      message: "Error trying to fetch the Comments data for this Issue On Item",
      error,
    })

    throw error
  }

  return false
}

export async function getIssueOnItemCommentListData(
  config?: HttpConfig
): Promise<IssueOnItemCommentDocument[]> {
  const db = await getDB()
  await fetchIssueOnItemCommentListData(config, db)
  return await db.issue_on_item_comments.find().exec()
}

//endregion

//region Attributes

export async function fetchAttributeCharListData(
  config?: HttpConfig,
  db?: AppBaseDB
): Promise<boolean> {
  const apiCallStore = useApiCallStore()
  const callUrl = CSIurl.ATTRIBUTES_CHAR_LIST

  try {
    if (apiCallStore.canCallAgain(callUrl)) {
      if (!db) db = await getDB()

      const lastCall = apiCallStore.getLastUpdatedTimeStamp(callUrl)

      const entities = await getAllEntitiesConcurrent<AttributeChar>(
        getAttributeCharList,
        { lastCall, callUrl },
        config
      )

      if (entities.length > 0) {
        await db.attribute_char.bulkUpsert(entities)
        return true
      }
    }
  } catch (error) {
    if (isIgnoredError(error)) return false
    Log.error({
      message: "Error trying to fetch Attribute Char data",
      error,
    })

    throw error
  }

  return false
}

export async function getAttributeCharListData(
  config?: HttpConfig
): Promise<RxDocument<AttributeChar>[]> {
  const db = await getDB()
  await fetchAttributeCharListData(config, db)
  return await db.attribute_char.find().exec()
}

export async function fetchAttributeNumericListData(
  config?: HttpConfig,
  db?: AppBaseDB
): Promise<boolean> {
  const apiCallStore = useApiCallStore()
  const callUrl = CSIurl.ATTRIBUTES_NUMERIC_LIST

  try {
    if (apiCallStore.canCallAgain(callUrl)) {
      if (!db) db = await getDB()

      const lastCall = apiCallStore.getLastUpdatedTimeStamp(callUrl)

      const entities = await getAllEntitiesConcurrent<AttributeNumeric>(
        getAttributeNumericList,
        { lastCall, callUrl },
        config
      )

      if (entities.length > 0) {
        await db.attribute_numeric.bulkUpsert(entities)
        return true
      }
    }
  } catch (error) {
    if (isIgnoredError(error)) return false
    Log.error({
      message: "Error trying to fetch Attribute Numeric data",
      error,
    })

    throw error
  }

  return false
}

export async function getAttributeNumericListData(
  config?: HttpConfig
): Promise<RxDocument<AttributeNumeric>[]> {
  const db = await getDB()
  await fetchAttributeNumericListData(config, db)
  return await db.attribute_numeric.find().exec()
}

export async function fetchAttributeSelectListData(
  config?: HttpConfig,
  db?: AppBaseDB
): Promise<boolean> {
  const apiCallStore = useApiCallStore()
  const callUrl = CSIurl.ATTRIBUTES_SELECT_LIST

  try {
    if (apiCallStore.canCallAgain(callUrl)) {
      if (!db) db = await getDB()

      const lastCall = apiCallStore.getLastUpdatedTimeStamp(callUrl)

      const entities = await getAllEntitiesConcurrent<AttributeSelect>(
        getAttributeSelectList,
        { lastCall, callUrl },
        config
      )

      if (entities.length > 0) {
        await db.attribute_select.bulkUpsert(entities)
        return true
      }
    }
  } catch (error) {
    if (isIgnoredError(error)) return false
    Log.error({
      message: "Error trying to fetch Attribute Select data",
      error,
    })

    throw error
  }

  return false
}

export async function getAttributeSelectListData(
  config?: HttpConfig
): Promise<RxDocument<AttributeSelect, AttributeSelectDocumentMethods>[]> {
  const db = await getDB()
  await fetchAttributeSelectListData(config, db)
  return await db.attribute_select.find().exec()
}

export async function fetchAttributeSelectOptionsListData(
  config?: HttpConfig,
  db?: AppBaseDB
): Promise<boolean> {
  const apiCallStore = useApiCallStore()
  const callUrl = CSIurl.ATTRIBUTES_SELECT_OPTIONS_LIST

  try {
    if (apiCallStore.canCallAgain(callUrl)) {
      if (!db) db = await getDB()

      const lastCall = apiCallStore.getLastUpdatedTimeStamp(callUrl)

      const entities = await getAllEntitiesConcurrent<AttributeSelectOptions>(
        getAttributesSelectOptionsList,
        { lastCall, callUrl },
        config
      )

      if (entities.length > 0) {
        await db.attribute_select_options.bulkUpsert(entities)
        return true
      }
    }
  } catch (error) {
    if (isIgnoredError(error)) return false
    Log.error({
      message: "Error trying to fetch Attribute Select Options data",
      error,
    })

    throw error
  }

  return false
}

export async function getAttributeSelectOptionsListData(
  config?: HttpConfig
): Promise<RxDocument<AttributeSelectOptions>[]> {
  const db = await getDB()
  await fetchAttributeSelectOptionsListData(config, db)
  return await db.attribute_select_options.find().exec()
}

export async function fetchAttributeTemporalListData(
  config?: HttpConfig,
  db?: AppBaseDB
): Promise<boolean> {
  const apiCallStore = useApiCallStore()
  const callUrl = CSIurl.ATTRIBUTES_TEMPORAL_LIST

  try {
    if (apiCallStore.canCallAgain(callUrl)) {
      if (!db) db = await getDB()

      const lastCall = apiCallStore.getLastUpdatedTimeStamp(callUrl)

      const entities = await getAllEntitiesConcurrent<AttributeTemporal>(
        getAttributeTemporalList,
        { lastCall, callUrl },
        config
      )

      if (entities.length > 0) {
        await db.attribute_temporal.bulkUpsert(entities)
        return true
      }
    }
  } catch (error) {
    if (isIgnoredError(error)) return false
    Log.error({
      message: "Error trying to fetch Attribute Temporal data",
      error,
    })

    throw error
  }

  return false
}

export async function getAttributeTemporalListData(
  config?: HttpConfig
): Promise<RxDocument<AttributeTemporal>[]> {
  const db = await getDB()
  await fetchAttributeTemporalListData(config, db)
  return await db.attribute_temporal.find().exec()
}

//endregion

//region Performed Work

export async function fetchCorrectiveWorkOnItemListData(
  config?: HttpConfig,
  db?: AppBaseDB
): Promise<boolean> {
  const apiCallStore = useApiCallStore()
  const callUrl = CSIurl.PERFORMED_WORK_CORRECTIVE_ITEM_LIST

  try {
    if (apiCallStore.canCallAgain(callUrl)) {
      if (!db) db = await getDB()

      const lastCall = apiCallStore.getLastUpdatedTimeStamp(callUrl)

      const entities = await getAllEntitiesConcurrent<CorrectiveWorkOnItem>(
        getCorrectiveWorkOnItemList,
        { lastCall, callUrl },
        config
      )

      if (entities.length > 0) {
        await db.corrective_work_on_item.bulkUpsert(entities)
        return true
      }
    }
  } catch (error) {
    if (isIgnoredError(error)) return false
    Log.error({
      message: "Error trying to fetch Corrective Work on Item data",
      error,
    })

    throw error
  }

  return false
}

export async function getCorrectiveWorkOnItemListData(
  config?: HttpConfig
): Promise<CorrectiveWorkOnItemDocument[]> {
  const db = await getDB()
  await fetchCorrectiveWorkOnItemListData(config, db)
  return await db.corrective_work_on_item.find().exec()
}

export async function fetchRegularWorkOnItemListData(
  config?: HttpConfig,
  db?: AppBaseDB
): Promise<boolean> {
  const apiCallStore = useApiCallStore()
  const callUrl = CSIurl.PERFORMED_WORK_REGULAR_ITEM_LIST

  try {
    if (apiCallStore.canCallAgain(callUrl)) {
      if (!db) db = await getDB()

      const lastCall = apiCallStore.getLastUpdatedTimeStamp(callUrl)

      const entities = await getAllEntitiesConcurrent<RegularWorkOnItem>(
        getRegularWorkOnItemList,
        { lastCall, callUrl },
        config
      )

      if (entities.length > 0) {
        await db.regular_work_on_item.bulkUpsert(entities)
        return true
      }
    }
  } catch (error) {
    if (isIgnoredError(error)) return false
    Log.error({
      message: "Error trying to fetch Regular Work on Item data",
      error,
    })

    throw error
  }

  return false
}

export async function getRegularWorkOnItemListData(
  config?: HttpConfig
): Promise<RegularWorkOnItemDocument[]> {
  const db = await getDB()
  await fetchRegularWorkOnItemListData(config, db)
  return await db.regular_work_on_item.find().exec()
}

//endregion

//region Work Domain Global

export async function fetchWorkDomainGlobalListData(
  config?: HttpConfig,
  commonDb?: AppCommonDB
): Promise<boolean> {
  const apiCallStore = useApiCallStore()
  const callUrl = SpringboardURL.WORK_DOMAINS

  try {
    if (apiCallStore.canCallAgain(callUrl)) {
      if (!commonDb) commonDb = await getCommonDB()

      const lastCall = apiCallStore.getLastUpdatedTimeStamp(callUrl)

      const entities = await getAllEntitiesConcurrent<WorkDomainGlobal>(
        getWorkDomainsGlobalList,
        { lastCall, callUrl },
        config
      )

      if (entities.length > 0) {
        await commonDb.work_domain_global.bulkUpsert(entities)
        return true
      }
    }
  } catch (error) {
    if (isIgnoredError(error)) return false
    Log.error({
      message: "Error trying to fetch Work Domain Global data",
      error,
    })

    throw error
  }

  return false
}

export async function getWorkDomainGlobalListData(
  config?: HttpConfig
): Promise<WorkDomainGlobalDocument[]> {
  const commonDb = await getCommonDB()
  await fetchWorkDomainGlobalListData(config, commonDb)
  return await commonDb.work_domain_global.find().exec()
}

//endregion

//region Work Domain Workspace

export async function fetchWorkDomainWorkspaceListData(
  config?: HttpConfig,
  db?: AppBaseDB
): Promise<boolean> {
  const apiCallStore = useApiCallStore()
  const callUrl = CSIurl.WORK_DOMAINS

  try {
    if (apiCallStore.canCallAgain(callUrl)) {
      if (!db) db = await getDB()

      const lastCall = apiCallStore.getLastUpdatedTimeStamp(callUrl)

      const entities = await getAllEntitiesConcurrent<WorkDomainWorkspace>(
        getWorkDomainsWorkspaceList,
        { lastCall, callUrl },
        config
      )

      if (entities.length > 0) {
        await db.work_domain_workspace.bulkUpsert(entities)
        return true
      }
    }
  } catch (error) {
    if (isIgnoredError(error)) return false
    Log.error({
      message: "Error trying to fetch Work Domain Workspace data",
      error,
    })

    throw error
  }

  return false
}

export async function getWorkDomain(
  workDomainId: string
): Promise<WorkDomainWorkspace | undefined> {
  const db = await getDB()
  const document = await db.work_domain_workspace.findOne({ selector: { id: workDomainId } }).exec()
  return document?.toMutableJSON()
}

export async function getWorkDomainWorkspaceListData(
  config?: HttpConfig
): Promise<WorkDomainWorkspaceDocument[]> {
  const db = await getDB()
  await fetchWorkDomainWorkspaceListData(config, db)
  return await db.work_domain_workspace.find().exec()
}

//endregion

//region Work Order

export async function fetchWorkOrderCorrectiveItemListData(
  config?: HttpConfig,
  db?: AppBaseDB
): Promise<boolean> {
  const apiCallStore = useApiCallStore()
  const callUrl = CSIurl.WORK_ORDER_CORRECTIVE_ON_ITEM

  try {
    if (apiCallStore.canCallAgain(callUrl)) {
      if (!db) db = await getDB()

      const lastCall = apiCallStore.getLastUpdatedTimeStamp(callUrl)

      const entities = await getAllEntitiesConcurrent<WorkOrderCorrectiveItem>(
        getWorkOrderCorrectiveItemList,
        { lastCall, callUrl },
        config
      )

      if (entities.length > 0) {
        await db.work_order_corrective_item.bulkUpsert(entities)
        return true
      }
    }
  } catch (error) {
    if (isIgnoredError(error)) return false
    Log.error({
      message: "Error trying to fetch Corrective Work Order Item data",
      error,
    })

    throw error
  }

  return false
}

export async function getWorkOrderCorrectiveItemListData(
  config?: HttpConfig
): Promise<WorkOrderCorrectiveItemDocument[]> {
  const db = await getDB()
  await fetchWorkOrderCorrectiveItemListData(config, db)
  return await db.work_order_corrective_item.find().exec()
}

export async function fetchWorkOrderCorrectiveItemCommentListData(
  config?: HttpConfig,
  db?: AppBaseDB
): Promise<boolean> {
  const apiCallStore = useApiCallStore()
  const callUrl = CSIurl.WORK_ORDER_CORRECTIVE_ON_ITEM_COMMENTS

  try {
    if (apiCallStore.canCallAgain(callUrl)) {
      if (!db) db = await getDB()

      const lastCall = apiCallStore.getLastUpdatedTimeStamp(callUrl)

      const entities = await getAllEntitiesConcurrent<WorkOrderCorrectiveItemComment>(
        getWorkOrderCorrectiveItemCommentList,
        { lastCall, callUrl },
        config
      )

      if (entities.length > 0) {
        await db.work_order_corrective_comments.bulkUpsert(entities)
        return true
      }
    }
  } catch (error) {
    if (isIgnoredError(error)) return false
    Log.error({
      message: "Error trying to fetch the Comments data for Corrective Work Order Item",
      error,
    })

    throw error
  }

  return false
}

export async function getWorkOrderCorrectiveItemCommentListData(
  config?: HttpConfig
): Promise<WorkOrderCorrectiveItemCommentDocument[]> {
  const db = await getDB()
  await fetchWorkOrderCorrectiveItemCommentListData(config, db)
  return await db.work_order_corrective_comments.find().exec()
}

export async function fetchWorkOrderRegularItemListData(
  config?: HttpConfig,
  db?: AppBaseDB
): Promise<boolean> {
  const apiCallStore = useApiCallStore()
  const callUrl = CSIurl.WORK_ORDER_REGULAR_ON_ITEM

  try {
    if (apiCallStore.canCallAgain(callUrl)) {
      if (!db) db = await getDB()

      const lastCall = apiCallStore.getLastUpdatedTimeStamp(callUrl)

      const entities = await getAllEntitiesConcurrent<WorkOrderRegularItem>(
        getWorkOrderRegularItemList,
        { lastCall, callUrl },
        config
      )

      if (entities.length > 0) {
        await db.work_order_regular_item.bulkUpsert(entities)
        return true
      }
    }
  } catch (error) {
    if (isIgnoredError(error)) return false
    Log.error({
      message: "Error trying to fetch Regular Work Order Item data",
      error,
    })

    throw error
  }

  return false
}

export async function getWorkOrderRegularItemListData(
  config?: HttpConfig
): Promise<WorkOrderRegularItemDocument[]> {
  const db = await getDB()
  await fetchWorkOrderRegularItemListData(config, db)
  return await db.work_order_regular_item.find().exec()
}

export async function fetchWorkOrderRegularItemCommentListData(
  config?: HttpConfig,
  db?: AppBaseDB
): Promise<boolean> {
  const apiCallStore = useApiCallStore()
  const callUrl = CSIurl.WORK_ORDER_REGULAR_ON_ITEM_COMMENTS

  try {
    if (apiCallStore.canCallAgain(callUrl)) {
      if (!db) db = await getDB()

      const lastCall = apiCallStore.getLastUpdatedTimeStamp(callUrl)

      const entities = await getAllEntitiesConcurrent<WorkOrderRegularItemComment>(
        getWorkOrderRegularItemCommentList,
        { lastCall, callUrl },
        config
      )

      if (entities.length > 0) {
        await db.work_order_regular_comments.bulkUpsert(entities)
        return true
      }
    }
  } catch (error) {
    if (isIgnoredError(error)) return false
    Log.error({
      message: "Error trying to fetch the Comments data for Regular Work Order Item",
      error,
    })

    throw error
  }

  return false
}

export async function getWorkOrderRegularItemCommentListData(
  config?: HttpConfig
): Promise<WorkOrderRegularItemCommentDocument[]> {
  const db = await getDB()
  await fetchWorkOrderRegularItemCommentListData(config, db)
  return await db.work_order_regular_comments.find().exec()
}

//endregion

//region Work Order Priority

export async function fetchWorkOrderPriorityListData(
  config?: HttpConfig,
  db?: AppBaseDB
): Promise<boolean> {
  const apiCallStore = useApiCallStore()
  const callUrl = CSIurl.WORK_ORDER_PRIORITIES

  try {
    if (apiCallStore.canCallAgain(callUrl)) {
      if (!db) db = await getDB()

      const lastCall = apiCallStore.getLastUpdatedTimeStamp(callUrl)

      const entities = await getAllEntitiesConcurrent<WorkOrderPriority>(
        getWorkOrderPrioritiesList,
        { lastCall, callUrl },
        config
      )

      if (entities.length > 0) {
        await db.work_order_priority.bulkUpsert(entities)
        return true
      }
    }
  } catch (error) {
    if (isIgnoredError(error)) return false
    Log.error({
      message: "Error trying to fetch Work Order Priorities data",
      error,
    })

    throw error
  }

  return false
}

export async function getWorkOrderPriorityListData(
  config?: HttpConfig
): Promise<WorkOrderPriorityDocument[]> {
  const db = await getDB()
  await fetchWorkOrderPriorityListData(config, db)
  return await db.work_order_priority.find().exec()
}

//endregion

//region Attachments

/**
 * DO NOT USE THIS UNLESS YOU KNOW WHAT YOU ARE DOING.
 *
 * This is a general function for attachments.
 * BUT attachments each have some context/entity they are attached to.
 * This means that each attachment has a different permission level.
 * Ergo, this function is only really usable by people with the highest permission levels,
 * and we should avoid using it as much as possible, unless strictly necessary.
 * One instance would be the attachments list in technician mode.
 * But this needs to be discussed further with the CTO and team leads.
 */
export async function fetchAttachmentListData(
  config?: HttpConfig,
  db?: AppBaseDB
): Promise<boolean> {
  const apiCallStore = useApiCallStore()
  const callUrl = CSIurl.ATTACHMENTS_LIST

  try {
    if (apiCallStore.canCallAgain(callUrl)) {
      if (!db) db = await getDB()

      const lastCall = apiCallStore.getLastUpdatedTimeStamp(callUrl)

      const entities = await getAllEntitiesConcurrent<Attachment>(
        getAttachmentsList,
        { lastCall, callUrl },
        config
      )

      if (entities.length > 0) {
        await db.attachment.bulkUpsert(entities)
        return true
      }
    }
  } catch (error) {
    if (isIgnoredError(error)) return false
    Log.error({
      message: "Error trying to fetch Attachment data",
      error,
    })

    throw error
  }

  return false
}

/**
 * DO NOT USE THIS UNLESS YOU KNOW WHAT YOU ARE DOING.
 * See {@link fetchAttachmentListData} for more information.
 */
export async function getAttachmentListData(config?: HttpConfig): Promise<AttachmentDocument[]> {
  const db = await getDB()
  await fetchAttachmentListData(config, db)
  return await db.attachment.find().exec()
}

//endregion

//region Identifier

export async function fetchIdentifierListData(
  config?: HttpConfig,
  db?: AppBaseDB
): Promise<boolean> {
  const apiCallStore = useApiCallStore()
  const callUrl = CSIurl.IDENTIFIERS_LIST

  try {
    if (apiCallStore.canCallAgain(callUrl)) {
      if (!db) db = await getDB()

      const data = await getIdentifiersList(config)
      if (data) {
        await db.identifier.bulkInsertByType(data)
        return true
      }
    }
  } catch (error) {
    if (isIgnoredError(error)) return false
    Log.error({
      message: "Error trying to fetch Identifier data",
      error,
    })

    throw error
  }

  return false
}

//endregion

//region Attachments

async function fetchAttachmentsForEntity(
  entityId: string,
  callUrl: string,
  getAttachmentFunc: (config: HttpConfig, entityId: string) => Promise<ObjectList<Attachment>>,
  context: AttachmentContext
) {
  const apiCallStore = useApiCallStore()

  try {
    if (apiCallStore.canCallAgain(callUrl)) {
      const db = await getDB()

      const lastCall = apiCallStore.getLastUpdatedTimeStamp(callUrl)

      const entities = await getAllAttachmentForEntity(
        getAttachmentFunc,
        { lastCall, callUrl },
        entityId
      )

      if (entities.length > 0) {
        await db.attachment.bulkUpsert(entities)
        return true
      }
    }
  } catch (error) {
    if (isIgnoredError(error)) return false
    Log.error({
      message: "Error trying to fetch Attachment data for " + context,
      error,
    })

    throw error
  }

  return false
}

export async function fetchAttachmentsForItem(itemId: string) {
  const url = CSIurl.ITEMS_ID_ATTACHMENTS_LIST.replace("{item_id}", itemId)
  return fetchAttachmentsForEntity(itemId, url, getItemAttachmentList, "item")
}

export async function fetchAttachmentsForItemModel(itemModelId: string) {
  const url = CSIurl.ITEM_MODELS_ID_ATTACHMENTS_LIST.replace("{model_id}", itemModelId)
  return fetchAttachmentsForEntity(itemModelId, url, getItemModelAttachmentList, "item_model")
}

export async function fetchAttachmentsForCorrectiveWorkOnItem(workId: string) {
  const url = CSIurl.PERFORMED_WORK_CORRECTIVE_ITEM_ATTACHMENTS_LIST.replace("{work_id}", workId)
  return fetchAttachmentsForEntity(
    workId,
    url,
    getCorrectiveWorkOnItemAttachmentList,
    "corrective_work_item"
  )
}

export async function fetchAttachmentsForRegularWorkOnItem(workId: string) {
  const url = CSIurl.PERFORMED_WORK_REGULAR_ITEM_ATTACHMENTS_LIST.replace("{work_id}", workId)
  return fetchAttachmentsForEntity(
    workId,
    url,
    getRegularWorkOnItemAttachmentList,
    "regular_work_item"
  )
}

export async function fetchAttachmentsForCorrectiveWorkOnItemComments(commentId: string) {
  const url = CSIurl.PERFORMED_WORK_CORRECTIVE_ITEM_COMMENTS_ATTACHMENTS.replace(
    "{comment_id}",
    commentId
  )
  return fetchAttachmentsForEntity(
    commentId,
    url,
    getCorrectiveWorkOnItemCommentAttachmentList,
    "corrective_work_item_comment"
  )
}

export async function fetchAttachmentsForRegularWorkOnItemComments(commentId: string) {
  const url = CSIurl.PERFORMED_WORK_REGULAR_ITEM_COMMENTS_ATTACHMENTS.replace(
    "{comment_id}",
    commentId
  )
  return fetchAttachmentsForEntity(
    commentId,
    url,
    getRegularWorkOnItemCommentAttachmentList,
    "regular_work_item_comment"
  )
}

export async function fetchAttachmentsForIssueOnItem(issueId: string) {
  const url = CSIurl.ISSUES_ON_ITEM_ATTACHMENTS_LIST.replace("{issue_id}", issueId)
  return fetchAttachmentsForEntity(issueId, url, getIssueOnItemAttachmentList, "issue_item")
}

export async function fetchAttachmentsForIssueOnItemComment(commentId: string) {
  const url = CSIurl.ISSUES_ON_ITEM_COMMENTS_ATTACHMENTS.replace("{comment_id}", commentId)
  return fetchAttachmentsForEntity(
    commentId,
    url,
    getIssueOnItemCommentAttachmentList,
    "issue_item_comment"
  )
}

export async function fetchAttachmentsForCorrectiveWorkOrderItem(workOrderId: string) {
  const url = CSIurl.WORK_ORDER_CORRECTIVE_ON_ITEM_ATTACHMENTS.replace("{wo_id}", workOrderId)
  return fetchAttachmentsForEntity(
    workOrderId,
    url,
    getWorkOrderCorrectiveItemAttachmentList,
    "work_order_corrective_item"
  )
}

export async function fetchAttachmentsForRegularWorkOrderItem(workOrderId: string) {
  const url = CSIurl.WORK_ORDER_REGULAR_ON_ITEM_ATTACHMENTS.replace("{wo_id}", workOrderId)
  return fetchAttachmentsForEntity(
    workOrderId,
    url,
    getWorkOrderRegularItemAttachmentList,
    "work_order_regular_item"
  )
}

export async function fetchAttachmentsForCorrectiveWorkOrderItemComment(commentId: string) {
  const url = CSIurl.WORK_ORDER_CORRECTIVE_ON_ITEM_COMMENTS_ATTACHMENTS.replace(
    "{comment_id}",
    commentId
  )
  return fetchAttachmentsForEntity(
    commentId,
    url,
    getWorkOrderCorrectiveItemCommentAttachmentList,
    "work_order_corrective_item_comment"
  )
}

export async function fetchAttachmentsForRegularWorkOrderItemComment(commentId: string) {
  const url = CSIurl.WORK_ORDER_REGULAR_ON_ITEM_COMMENTS_ATTACHMENTS.replace(
    "{comment_id}",
    commentId
  )
  return fetchAttachmentsForEntity(
    commentId,
    url,
    getWorkOrderRegularItemCommentAttachmentList,
    "work_order_regular_item_comment"
  )
}

/**
 * Call the appropriate post function for attachment based on the entity provided.
 * This functions does NO error handling, this should be done on use level.
 * Returns NULL for cases of entity not yet supported.
 *
 * @param entity
 * @param entityId
 * @param attachment
 */
export async function postAttachmentForEntity(
  entity: AttachmentContext,
  entityId: string,
  attachment: AttachmentUpload
): Promise<Attachment | null> {
  switch (entity) {
    case "item":
      return postItemAttachment({}, entityId, attachment)

    case "item_model":
      return postItemModelAttachment({}, entityId, attachment)

    case "issue_item":
      return postIssueOnItemAttachment({}, entityId, attachment)

    case "corrective_work_item":
      return postCorrectiveWorkOnItemAttachment({}, entityId, attachment)

    case "regular_work_item":
      return postRegularWorkOnItemAttachment({}, entityId, attachment)

    case "issue_item_comment":
      return postIssueOnItemCommentAttachment({}, entityId, attachment)

    case "corrective_work_item_comment":
      return postCorrectiveWorkOnItemCommentAttachment({}, entityId, attachment)

    case "regular_work_item_comment":
      return postRegularWorkOnItemCommentAttachment({}, entityId, attachment)

    case "work_order_corrective_item":
      return postWorkOrderCorrectiveItemAttachment({}, entityId, attachment)

    case "work_order_regular_item":
      return postWorkOrderRegularItemAttachment({}, entityId, attachment)

    case "work_order_corrective_item_comment":
      return postWorkOrderCorrectiveItemCommentAttachment({}, entityId, attachment)

    case "work_order_regular_item_comment":
      return postWorkOrderRegularItemCommentAttachment({}, entityId, attachment)

    case "work_order_corrective_area":
    case "work_order_regular_area":
    case "work_order_corrective_area_comment":
    case "work_order_regular_area_comment":
    case "regular_work_area":
    case "corrective_work_area":
    case "issue_area":
    case "area":
    case "zone":
      return null
  }
}

/**
 * Call the appropriate post batch function for attachment based on the entity provided.
 * This functions does NO error handling, this should be done on use level.
 * Returns NULL for cases of entity not yet supported.
 *
 * @param entity
 * @param entityId
 * @param attachments
 */
export async function postAttachmentForEntityBatch(
  entity: AttachmentContext,
  entityId: string,
  attachments: AttachmentUpload[]
): Promise<ObjectList<Attachment> | ObjectList<BatchErrors | Attachment> | null> {
  switch (entity) {
    case "item":
      return postItemAttachmentBatch({}, entityId, attachments)

    case "item_model":
      return postItemModelAttachmentBatch({}, entityId, attachments)

    case "issue_item":
      return postIssueOnItemAttachmentBatch({}, entityId, attachments)

    case "corrective_work_item":
      return postCorrectiveWorkOnItemAttachmentBatch({}, entityId, attachments)

    case "regular_work_item":
      return postRegularWorkOnItemAttachmentBatch({}, entityId, attachments)

    case "issue_item_comment":
      return postIssueOnItemCommentAttachmentBatch({}, entityId, attachments)

    case "corrective_work_item_comment":
      return postCorrectiveWorkOnItemCommentAttachmentBatch({}, entityId, attachments)

    case "regular_work_item_comment":
      return postRegularWorkOnItemCommentAttachmentBatch({}, entityId, attachments)

    case "work_order_corrective_item":
      return postWorkOrderCorrectiveItemAttachmentBatch({}, entityId, attachments)

    case "work_order_regular_item":
      return postWorkOrderRegularItemAttachmentBatch({}, entityId, attachments)

    case "work_order_corrective_item_comment":
      return postWorkOrderCorrectiveItemCommentAttachmentBatch({}, entityId, attachments)

    case "work_order_regular_item_comment":
      return postWorkOrderRegularItemCommentAttachmentBatch({}, entityId, attachments)

    case "work_order_corrective_area":
    case "work_order_regular_area":
    case "work_order_corrective_area_comment":
    case "work_order_regular_area_comment":
    case "regular_work_area":
    case "corrective_work_area":
    case "issue_area":
    case "area":
    case "zone":
      return null
  }
}

/**
 * Call the appropriate get attachment function for attachment based on the entity provided.
 * This functions does NO error handling, this should be done on use level.
 * Returns NULL for cases of entity not yet supported.
 *
 * @param entity
 * @param entityId
 * @param attachmentId
 */
export async function fetchAttachmentsListForEntity(
  entity: AttachmentContext,
  entityId: string
): Promise<boolean | null> {
  switch (entity) {
    case "item":
      return fetchAttachmentsForItem(entityId)

    case "item_model":
      return fetchAttachmentsForItemModel(entityId)

    case "issue_item":
      return fetchAttachmentsForIssueOnItem(entityId)

    case "corrective_work_item":
      return fetchAttachmentsForCorrectiveWorkOnItem(entityId)

    case "regular_work_item":
      return fetchAttachmentsForRegularWorkOnItem(entityId)

    case "issue_item_comment":
      return fetchAttachmentsForIssueOnItemComment(entityId)

    case "corrective_work_item_comment":
      return fetchAttachmentsForCorrectiveWorkOrderItemComment(entityId)

    case "regular_work_item_comment":
      return fetchAttachmentsForRegularWorkOrderItemComment(entityId)

    case "work_order_corrective_item":
      return fetchAttachmentsForCorrectiveWorkOrderItem(entityId)

    case "work_order_regular_item":
      return fetchAttachmentsForRegularWorkOrderItem(entityId)

    case "work_order_corrective_item_comment":
      return fetchAttachmentsForCorrectiveWorkOrderItemComment(entityId)

    case "work_order_regular_item_comment":
      return fetchAttachmentsForRegularWorkOrderItemComment(entityId)

    case "work_order_corrective_area":
    case "work_order_regular_area":
    case "work_order_corrective_area_comment":
    case "work_order_regular_area_comment":
    case "regular_work_area":
    case "corrective_work_area":
    case "issue_area":
    case "area":
    case "zone":
      return null
  }
}

/**
 * Call the appropriate get attachment function for attachment based on the entity provided.
 * This functions does NO error handling, this should be done on use level.
 * Returns NULL for cases of entity not yet supported.
 *
 * @param entity
 * @param entityId
 * @param attachmentId
 */
export async function getAttachmentForEntity(
  entity: AttachmentContext,
  entityId: string,
  attachmentId: string
): Promise<Attachment | null> {
  switch (entity) {
    case "item":
      return getItemAttachment({}, entityId, attachmentId)

    case "item_model":
      return getItemModelAttachment({}, entityId, attachmentId)

    case "issue_item":
      return getIssueOnItemAttachment({}, entityId, attachmentId)

    case "corrective_work_item":
      return getCorrectiveWorkOnItemAttachment({}, entityId, attachmentId)

    case "regular_work_item":
      return getRegularWorkOnItemAttachment({}, entityId, attachmentId)

    case "issue_item_comment":
      return getIssueOnItemCommentAttachment({}, entityId, attachmentId)

    case "corrective_work_item_comment":
      return getCorrectiveWorkOnItemCommentAttachment({}, entityId, attachmentId)

    case "regular_work_item_comment":
      return getRegularWorkOnItemCommentAttachment({}, entityId, attachmentId)

    case "work_order_corrective_item":
      return getWorkOrderCorrectiveItemAttachment({}, entityId, attachmentId)

    case "work_order_regular_item":
      return getWorkOrderRegularItemAttachment({}, entityId, attachmentId)

    case "work_order_corrective_item_comment":
      return getWorkOrderCorrectiveItemCommentAttachment({}, entityId, attachmentId)

    case "work_order_regular_item_comment":
      return getWorkOrderRegularItemCommentAttachment({}, entityId, attachmentId)

    case "work_order_corrective_area":
    case "work_order_regular_area":
    case "work_order_corrective_area_comment":
    case "work_order_regular_area_comment":
    case "regular_work_area":
    case "corrective_work_area":
    case "issue_area":
    case "area":
    case "zone":
      return null
  }
}

/**
 * Call the appropriate get content function for attachment based on the entity provided.
 * This functions does NO error handling, this should be done on use level.
 * Returns NULL for cases of entity not yet supported.
 *
 * @param entity
 * @param entityId
 * @param attachmentId
 */
export async function getAttachmentContentForEntity(
  entity: AttachmentContext,
  entityId: string,
  attachmentId: string
): Promise<Blob | null> {
  switch (entity) {
    case "item":
      return getItemAttachmentContent({}, entityId, attachmentId)

    case "item_model":
      return getItemModelAttachmentContent({}, entityId, attachmentId)

    case "issue_item":
      return getIssueOnItemAttachmentContent({}, entityId, attachmentId)

    case "corrective_work_item":
      return getCorrectiveWorkOnItemAttachmentContent({}, entityId, attachmentId)

    case "regular_work_item":
      return getRegularWorkOnItemAttachmentContent({}, entityId, attachmentId)

    case "issue_item_comment":
      return getIssueOnItemCommentAttachmentContent({}, entityId, attachmentId)

    case "corrective_work_item_comment":
      return getCorrectiveWorkOnItemCommentAttachmentContent({}, entityId, attachmentId)

    case "regular_work_item_comment":
      return getRegularWorkOnItemCommentAttachmentContent({}, entityId, attachmentId)

    case "work_order_corrective_item":
      return getWorkOrderCorrectiveItemAttachmentContent({}, entityId, attachmentId)

    case "work_order_regular_item":
      return getWorkOrderRegularItemAttachmentContent({}, entityId, attachmentId)

    case "work_order_corrective_item_comment":
      return getWorkOrderCorrectiveItemCommentAttachmentContent({}, entityId, attachmentId)

    case "work_order_regular_item_comment":
      return getWorkOrderRegularItemCommentAttachmentContent({}, entityId, attachmentId)

    case "work_order_corrective_area":
    case "work_order_regular_area":
    case "work_order_corrective_area_comment":
    case "work_order_regular_area_comment":
    case "regular_work_area":
    case "corrective_work_area":
    case "issue_area":
    case "area":
    case "zone":
      return null
  }
}

//endregion
